/**
 * product-mode.ts
 *
 * Shell selection by domain (per docs/auth/rf-handoff-spec.md).
 * Domain you visit = shell you see. Access (which domains a company may enter)
 * is decided separately by companies.product_mode via ProductModeGuard.
 *
 * Plan 159-02 shipped a stub returning 'full'. This file (plan 159-03) replaces
 * that stub with the real hostname-based detection and the D-14 branding swap.
 */

export type ProductMode = 'full' | 'feedback';

/**
 * Determine product shell from hostname.
 *
 * Returns 'feedback' when hostname is feedback-os.com or any *.feedback-os.com
 * (case-insensitive). Returns 'full' for everything else: sellercockpit.com,
 * localhost, 127.0.0.1, and *.pages.dev Cloudflare preview deployments.
 *
 * An optional hostname arg acts as a test seam so unit tests do not touch window.
 */
export function getProductMode(hostname: string = window.location.hostname): ProductMode {
  const h = hostname.toLowerCase();
  if (h === 'feedback-os.com' || h.endsWith('.feedback-os.com')) return 'feedback';
  // sellercockpit.com, localhost, 127.0.0.1, *.pages.dev previews all default to the full shell
  return 'full';
}

/**
 * Returns the brand name for the current product shell.
 *
 * 'Feedback OS' on the feedback shell, 'SellerCockpit' everywhere else.
 * Always delegates to getProductMode so hostname detection is never duplicated.
 *
 * An optional hostname arg acts as a test seam so unit tests do not touch window.
 */
export function getProductBrandName(hostname: string = window.location.hostname): string {
  return getProductMode(hostname) === 'feedback' ? 'Feedback OS' : 'SellerCockpit';
}

/**
 * D-14: per-domain favicon + tab title via runtime hostname swap (NOT an edge rewrite).
 *
 * On the feedback shell, repoint the static index.html favicon links and set the tab
 * title to the Feedback OS variants. On the full shell this is a no-op so SellerCockpit
 * assets and title are untouched.
 *
 * Uses getProductMode (the SAME hostname detection), never a second detection path.
 * Asset set produced by plan 02 under apps/web/public/logos/feedback-os/.
 *
 * An optional hostname arg acts as a test seam so unit tests do not touch window.
 */
export function applyProductBranding(hostname: string = window.location.hostname): void {
  if (getProductMode(hostname) !== 'feedback') return;

  document.title = 'Feedback OS';

  const setHref = (selector: string, href: string) => {
    const el = document.head.querySelector<HTMLLinkElement>(selector);
    if (el) el.href = href;
  };

  setHref('link[rel="icon"][type="image/svg+xml"]', '/logos/feedback-os/icon.svg');
  setHref('link[rel="icon"][sizes="any"]', '/logos/feedback-os/favicon-32x32.png');
  setHref('link[rel="apple-touch-icon"]', '/logos/feedback-os/apple-touch-icon-180x180.png');
}
